import { heatOptions, regionOptions, sortOptions, yearOptions, type SortKey } from "@/lib/videoData";

export interface FilterState {
  region: string;
  year: string;
  heat: string;
  sort: SortKey;
}

interface FilterPanelProps {
  filters: FilterState;
  onChange: (patch: Partial<FilterState>) => void;
  onReset: () => void;
  resultCount: number;
  showSort?: boolean;
}

const groups = [
  { key: "region" as const, label: "地区", options: regionOptions },
  { key: "year" as const, label: "年份", options: yearOptions },
  { key: "heat" as const, label: "热门程度", options: heatOptions },
];

export default function FilterPanel({
  filters,
  onChange,
  onReset,
  resultCount,
  showSort = false,
}: FilterPanelProps) {
  return (
    <div className="space-y-5">
      {groups.map((group) => (
        <div key={group.key}>
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs font-semibold text-foreground-900">{group.label}</p>
            {filters[group.key] !== "全部" && (
              <span className="flex items-center gap-0.5 text-[10px] font-medium text-accent-700">
                <i className="ri-check-line" />
                {filters[group.key]}
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {group.options.map((option) => {
              const active = filters[group.key] === option;
              return (
                <button
                  key={option}
                  type="button"
                  onClick={() => {
                    const patch: Partial<FilterState> =
                      group.key === "region"
                        ? { region: option }
                        : group.key === "year"
                          ? { year: option }
                          : { heat: option };
                    onChange(patch);
                  }}
                  className={`cursor-pointer whitespace-nowrap rounded-md px-2.5 py-1.5 text-[11px] font-medium transition-colors ${
                    active
                      ? "bg-primary-800 text-background-50"
                      : "bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-700"
                  }`}
                >
                  {option}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {showSort && (
        <div>
          <p className="mb-2 text-xs font-semibold text-foreground-900">排序方式</p>
          <div className="flex flex-wrap gap-1.5">
            {sortOptions.map((option) => {
              const active = filters.sort === option.key;
              return (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => onChange({ sort: option.key })}
                  className={`flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md px-2.5 py-1.5 text-[11px] font-medium transition-colors ${
                    active
                      ? "bg-accent-500 text-foreground-950"
                      : "bg-background-100 text-foreground-700 hover:bg-accent-100"
                  }`}
                >
                  <i className={`${option.icon} text-xs`} />
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="border-t border-background-200 pt-4">
        <p className="text-[11px] text-foreground-500">
          当前筛选共 <span className="font-semibold text-primary-700">{resultCount}</span> 部内容
        </p>
        <button
          type="button"
          onClick={onReset}
          className="mt-2.5 flex h-9 w-full cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 text-xs font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700"
        >
          <i className="ri-refresh-line text-sm" />
          重置全部筛选
        </button>
      </div>
    </div>
  );
}